import z from "zod";
import { emailRegex } from "../../../../../lib/regex";

export const updateVatSchema = z.object({
    vat: z.string()
        .refine((val) => {
            if (!val) return true;
            const cleaned = val.trim().replaceAll(/[\s\-\.]/g, "").toUpperCase();
            return /^(IT\d{11}|FR[A-Z0-9]{2}\d{9}|DE\d{9}|ES[A-Z]\d{7}[A-Z]|\d{11})$/.test(cleaned);
        }, {
            message: "Partita IVA non valida. Formato ammesso: IT/FR/DE/ES + codice"
        })
        .optional().nullable(),
})

export type UpdateVatFormValues = z.infer<typeof updateVatSchema>;

export const updateCompanySchema = z.object({
    revenue: z.number().min(0, "Il fatturato deve essere un numero positivo").optional().nullable(),
    phones: z.array(z.object({
        phone: z.string().refine((val) => {
            if (val === "") return true;
            let cleaned = val.trim();
            if (cleaned.startsWith("+39")) cleaned = cleaned.substring(3);
            else if (cleaned.startsWith("0039")) cleaned = cleaned.substring(4);
            cleaned = cleaned.replace(/[\s\-\.\/()\+]/g, "");
            return /^\d+$/.test(cleaned) &&
                cleaned.length >= 7 &&
                cleaned.length <= 11 &&
                (cleaned.startsWith("0") || cleaned === "011111111111");
        }, {
            message: "Telefono non valido: deve iniziare con 0 e avere 7-11 cifre"
        }),
        note: z.string().optional().nullable(),
    })).min(1, "È richiesto almeno un numero di telefono").optional().nullable(),

    emails: z.array(
        z.string().nullable().refine((val) => {
            if (val === "" || val === null || val === undefined) return true;
            return emailRegex.test(val) && !val.includes(" ");
        }, {
            message: "Email non valida"
        })
    ).optional().nullable(),

    address: z.string()
        .refine((val) => {
            if (!val || val === "") return true;
            return /.*[a-zA-Z].*/.test(val);
        }, {
            message: "Inserire un indirizzo valido"
        })
        .optional().nullable(),

    zipCode: z.string()
        .refine((val) => {
            if (!val || val === "") return true;
            const cleaned = val.trim();
            return /^\d+$/.test(cleaned) && cleaned.length <= 5;
        }, {
            message: "CAP non valido: deve contenere solo numeri (max 5 cifre)"
        })
        .optional().nullable(),

    city: z.string().optional().nullable(),
    province: z.string().optional().nullable(),
    countryRegion: z.string().optional().nullable(),
    state: z.string().optional().nullable(),

    ateco: z.object({
        atecoCode: z.string().optional().nullable(),
        atecoDescription: z.string().optional().nullable(),
        sector: z.string().optional().nullable(),
        category: z.string().optional().nullable(),
    }).optional().nullable(),

    ecommerce: z.boolean().optional().nullable(),

    employeesNumber: z.number()
        .min(0, "Il numero di dipendenti deve essere un numero positivo")
        .optional().nullable(),

    website: z.string()
        .refine((val) => {
            if (!val || val === "") return true;
            const cleaned = val.trim().toLowerCase();
            return cleaned.length >= 5 &&
                cleaned.length <= 255 &&
                !cleaned.includes(" ") &&
                cleaned.includes(".") &&
                !/[\@\#\!\$\%\^\&\*\(\)\=\+\{\}\[\]|\\:\";'<>?,~`]/.test(cleaned);
        }, {
            message: "URL non valido: lunghezza 5-255 caratteri, senza spazi e caratteri speciali"
        })
        .optional().nullable(),

    active: z.boolean().optional().nullable(),

    particularities: z.array(z.string())
        .refine((val) => {
            if (!val || val.length === 0) return true;
            return val.every(p => /^[\p{L}\p{N}]+$/u.test(p));
        }, {
            message: "Particolarità devono essere stringhe alfanumeriche senza spazi"
        })
        .optional().nullable(),

    missingFields: z.array(z.string()).optional().nullable(),
    uniqueCode: z.array(z.string()).optional().nullable(),
});

export type UpdateCompanyFormValues = z.infer<typeof updateCompanySchema>;